/**
 * Unicode normalization pipeline.
 *
 * Per IMPLEMENTATION_PLAN §13.5 (Eng patch: NFD alone doesn't strip combining
 * marks, it just decomposes them). The full pipeline:
 *
 *   1. NFD: decompose to base char + combining marks.
 *   2. Strip combining diacritics (Unicode block U+0300–U+036F).
 *   3. Lowercase.
 *   4. Strip everything that isn't a letter, digit, or space.
 *   5. Collapse runs of whitespace.
 *   6. Trim.
 *
 * Used for:
 *   - Word-match (PRD §3.6): guess vs target.
 *   - Custom word list parsing (M3.T9 / §5.10).
 *   - Player-name comparison (M3.T6 duplicate-detection).
 *
 * **Locked behavior: internal `\t` and `\n` are DROPPED, not collapsed.**
 * The non-letter strip in step 4 runs BEFORE the whitespace-collapse in
 * step 5. Tabs and newlines are not in `\p{L} ∪ \p{N} ∪ ASCII-space`,
 * so they vanish entirely; consecutive `\t\n` between letters does NOT
 * become a single space.
 *
 * Rationale (§13.5 lock; supersedes PRD §3.6 wording):
 *   - Guess input arrives as a single `<input>` line, embedded tabs/
 *     newlines are extremely rare.
 *   - Custom word-list parsing splits on `\n` BEFORE the normalizer sees
 *     the row (M3.T9 / §5.10), so each row is already a single line.
 *   - Both consumers are insensitive to the "tabs preserved as space"
 *     reading of PRD §3.6, so we lock to "tabs dropped", a one-pass
 *     pipeline, no special case.
 *
 * Property test: `normalize("Café") === normalize("Cafe") === "cafe"`.
 * Locked behavior test: `normalize("ice\t\ncream") === "icecream"`.
 */

/**
 * Combining diacritic range: U+0300 to U+036F. We use a literal regex so
 * the bundle doesn't pay for `\p{M}` (matches more, combining marks
 * elsewhere in Unicode), and so transpiled output stays tiny.
 */
const COMBINING_DIACRITICS = /[̀-ͯ]/g;

/**
 * Anything that is NOT a Unicode letter, Unicode digit, or ASCII space.
 * Uses `u` flag and `\p{L}` / `\p{N}` so cyrillic/greek/etc. survive.
 */
const NON_LETTER_OR_DIGIT_OR_SPACE = /[^\p{L}\p{N} ]/gu;

const WHITESPACE_RUN = /\s+/g;

export function normalize(input: string): string {
  return input
    .normalize("NFD")
    .replace(COMBINING_DIACRITICS, "")
    .toLowerCase()
    .replace(NON_LETTER_OR_DIGIT_OR_SPACE, "")
    .replace(WHITESPACE_RUN, " ")
    .trim();
}
